import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import { ArrowLeft, TrendingUp, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const CaseStudyFintech = () => {
  useEffect(() => {
    document.title = "FinTech Company Case Study - AI-Powered Customer Acquisition | NQ Marketing";
  }, []);

  return (
    <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 pt-32 pb-20">
          <Link to="/#case-studies">
            <Button variant="ghost" className="mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Button>
          </Link>

          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
                AI-Powered Marketing
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                FinTech Company Scales Customer Acquisition by 410%
              </h1>
              <div className="flex flex-wrap gap-6 text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>12-month project</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>FinTech Industry</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>410% Growth</span>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2>The Challenge</h2>
              <p>
                A mid-sized FinTech company offering digital banking solutions was facing scalability challenges with their marketing efforts. Their traditional marketing approach was labor-intensive, costly, and unable to keep pace with aggressive growth targets. They needed to acquire customers at scale while maintaining personalization and efficiency.
              </p>

              <h2>Our Approach</h2>
              <p>
                We implemented a comprehensive AI-powered marketing system that automated and optimized their entire customer acquisition funnel:
              </p>
              
              <h3>1. AI-Driven Lead Scoring & Segmentation</h3>
              <ul>
                <li>Deployed machine learning models to score leads based on 50+ behavioral signals</li>
                <li>Created dynamic audience segments that automatically updated in real-time</li>
                <li>Implemented predictive analytics to identify high-value prospects</li>
                <li>Built custom lookalike audiences using AI pattern recognition</li>
              </ul>

              <h3>2. Personalized Multi-Channel Campaigns</h3>
              <ul>
                <li>Launched AI-powered email personalization delivering unique content to each recipient</li>
                <li>Implemented dynamic landing pages that adapted to user behavior and demographics</li>
                <li>Created automated nurture sequences with intelligent send-time optimization</li>
                <li>Deployed chatbot with natural language processing for instant lead qualification</li>
              </ul>

              <h3>3. Predictive Analytics & Optimization</h3>
              <ul>
                <li>Built custom attribution model tracking customer journey across 12+ touchpoints</li>
                <li>Implemented AI-powered budget allocation optimizing spend across channels</li>
                <li>Created automated A/B testing framework running 100+ tests simultaneously</li>
                <li>Developed churn prediction model for proactive retention campaigns</li>
              </ul>

              <h2>The Results</h2>
              <div className="grid md:grid-cols-3 gap-6 my-8 not-prose">
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">410%</div>
                  <div className="text-muted-foreground">Customer Acquisition Growth</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">58%</div>
                  <div className="text-muted-foreground">CAC Reduction</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">340%</div>
                  <div className="text-muted-foreground">Marketing ROI Increase</div>
                </div>
              </div>

              <h2>Detailed Performance Metrics</h2>
              <ul>
                <li>Monthly qualified leads increased from 1,200 to 6,120</li>
                <li>Conversion rate improved from 2.3% to 8.7%</li>
                <li>Customer acquisition cost decreased from $487 to $204</li>
                <li>Time to conversion reduced by 45%</li>
                <li>Customer lifetime value increased by 67%</li>
                <li>Marketing team efficiency improved by 75% (time saved on manual tasks)</li>
              </ul>

              <h2>AI Tools & Technologies Implemented</h2>
              <ul>
                <li>Machine learning lead scoring models (Python/TensorFlow)</li>
                <li>Natural language processing for content personalization</li>
                <li>Predictive analytics for customer behavior forecasting</li>
                <li>Automated bidding algorithms for paid advertising</li>
                <li>Computer vision for creative testing and optimization</li>
              </ul>

              <h2>Key Takeaways</h2>
              <p>
                This case study demonstrates how AI and automation can transform marketing from a cost center into a scalable growth engine. The key to success was not just implementing technology, but thoughtfully integrating it into every stage of the customer journey.
              </p>
              <p>
                Critical insights from this project:
              </p>
              <ul>
                <li>AI enables personalization at scale that was previously impossible</li>
                <li>Predictive analytics shifts marketing from reactive to proactive</li>
                <li>Automation frees teams to focus on strategy and creativity</li>
                <li>Continuous optimization through AI delivers compounding returns</li>
                <li>Data integration is crucial—AI is only as good as the data it learns from</li>
              </ul>

              <div className="bg-primary/5 border-l-4 border-primary p-6 my-8">
                <p className="text-lg italic mb-2">
                  "NQ Marketing's AI-powered approach completely revolutionized our customer acquisition. We're now acquiring customers faster and more efficiently than ever before. The ROI has exceeded our most optimistic projections."
                </p>
                <p className="font-semibold">— CMO, FinTech Company</p>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t">
              <h3 className="text-2xl font-bold mb-4">Ready to leverage AI for growth?</h3>
              <p className="text-muted-foreground mb-6">
                Let's discuss how AI-powered marketing can transform your customer acquisition strategy.
              </p>
              <ScheduleConsultationDialog
                buttonText="Get Started"
                buttonSize="lg"
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
  );
};

export default CaseStudyFintech;
